
import { Modal } from 'bootstrap';
import { defineComponent, ref, inject, onMounted, watch } from 'vue';
import { Apollo, Notify } from '@/core/services';
import { GET_CUSTOMER_GROUP } from '@/modules/customer/customer-groups/graphql/Queries';
import { UPDATE_PRODUCT_GROUP } from '@/modules/catalog/product-groups/graphql/Mutations';
import { EditProductGroupForm } from '@/modules/catalog/product-groups/interfaces';
import { useI18n } from 'vue-i18n';
import InnerLoader from '@/components/InnerLoader.vue';

export default defineComponent({
    name: 'edit customer group',
    components: {
        InnerLoader
    },

    setup() {
        const modal: any = ref();
        const loading = ref(false);
        const emitter: any = inject('emitter');
        const current_page = ref(1);
        const i18n = useI18n();
        const editProductGroupForm = ref<null | HTMLFormElement>(null);
        const discountTypeError = ref('');

        // Given Edit Customer Group Form Interface
        const editProductGroupData = ref<EditProductGroupForm>({
            id: null,
            name: '',
            uuid: '',
            allow_discount: false,
            discount_type: '',
            discount_value: ''
        });

        // Clear discount type error when user selects a value
        const clearDiscountTypeError = () => {
            discountTypeError.value = '';
        };

        // Form validation rules
        const formRules = ref({
            name: [
                {
                    required: true,
                    message: `${i18n.t('message.NAME')} ${i18n.t('message.IS_REQUIRED')}`,
                    trigger: 'blur'
                }
            ],
            discount_type: [
                {
                    validator: (rule: any, value: string, callback: any) => {
                        if (editProductGroupData.value.allow_discount && !value) {
                            callback(new Error(`${i18n.t('message.DISCOUNT_TYPE')} ${i18n.t('message.IS_REQUIRED')}`));
                        } else {
                            callback();
                        }
                    },
                    trigger: 'change'
                }
            ],
            discount_value: [
                {
                    validator: (rule: any, value: string, callback: any) => {
                        if (editProductGroupData.value.allow_discount && !value) {
                            callback(new Error(`${i18n.t('message.DISCOUNT_VALUE')} ${i18n.t('message.IS_REQUIRED')}`));
                        } else {
                            callback();
                        }
                    },
                    trigger: 'blur'
                }
            ]
        });

        // Helper function to safely parse discount value
        const parseDiscountValue = (value: string): number | null => {
            if (!value || value.trim() === '') return null;
            const parsed = parseFloat(value);
            return isNaN(parsed) ? null : parsed;
        };

        // Submit Hanlder Request
        const submitHandlerForm = async () => {
            editProductGroupForm.value?.validate(async (valid: boolean) => {
                // Additional validation for discount fields when allow_discount is true
                if (editProductGroupData.value.allow_discount) {
                    if (!editProductGroupData.value.discount_type) {
                        discountTypeError.value = `${i18n.t('message.DISCOUNT_TYPE')} ${i18n.t('message.IS_REQUIRED')}`;
                        return;
                    }
                }

                if (valid) {
                    const formData = {
                        uuid: editProductGroupData.value.uuid,
                        name: editProductGroupData.value.name,
                        discount_type: editProductGroupData.value.allow_discount ? editProductGroupData.value.discount_type : null,
                        discount_value: editProductGroupData.value.allow_discount ? parseDiscountValue(editProductGroupData.value.discount_value) : null
                    };

                    loading.value = true;
                    await Apollo.mutate({
                        mutation: UPDATE_PRODUCT_GROUP,
                        variables: { input: formData },
                        update: (store, { data: { update_product_group } }) => {
                            modal.value.hide();
                            loading.value = false;
                            Notify.success(`${i18n.t('message.RECORD_UPDATED_SUCCESSFULLY')}`);
                        }
                    }).catch(() => {
                        loading.value = false;
                    });
                }
            });
        };

        // Reset Form Data On Model Popup
        const resetForm = () => {
            // Reset form data
            editProductGroupData.value = {
                id: null,
                name: '',
                uuid: '',
                allow_discount: false,
                discount_type: '',
                discount_value: ''
            };

            // Clear error messages
            discountTypeError.value = '';

            // Reset form fields if form is available
            if (editProductGroupForm.value) {
                editProductGroupForm.value.resetFields();
            }
        };

        // Run resetForm when component mounts
        onMounted(() => {
            resetForm();
        });

        // Emitter To Open Model
        emitter.on('editProductGroupAction', (product_group: Record<any, any>) => {
            resetForm();
            current_page.value = product_group.current_page;
            editProductGroupData.value.id = product_group.id;
            editProductGroupData.value.name = product_group.name;
            editProductGroupData.value.uuid = product_group.uuid;
            editProductGroupData.value.allow_discount = product_group.allow_discount || false;
            editProductGroupData.value.discount_type = product_group.discount_type || '';
            editProductGroupData.value.discount_value = product_group.discount_value || '';
            modal.value = new Modal(document.getElementById('modal_edit_product_group'));
            modal.value.show();
        });

        return {
            loading,
            editProductGroupData,
            editProductGroupForm,
            submitHandlerForm,
            discountTypeError,
            clearDiscountTypeError,
            formRules
        };
    }
});
